import { CubicBezierCurve3, CurvePath, Vector2, Vector3 } from 'three';

import { isVectorLeftOf } from 'shared/utils/vector-utils';

const Z_VECTOR_3 = new Vector3(0, 0, 1);

/**
 * Returns the path of bezier curves derived from the sequence of given control vertices
 *
 * @param {number[]} points2D A sequence of 2D control vertex coordinates
 * @param {number[]} scale The scale to multiply the positions with
 * @returns {CurvePath<Vector3>} The CurvePath created from given control vertices
 */
export const curvePathFromPoints = (points2D: number[], scale: Vector3): CurvePath<Vector3> => {
  const curve = new CurvePath<Vector3>();

  const lastX = points2D[points2D.length - 2];
  const lastY = points2D[points2D.length - 1];

  for (let i = 0; i < points2D.length - 2; i += 6) {
    const curveSection = new CubicBezierCurve3(
      new Vector3(points2D[i], points2D[i + 1], 0).multiply(scale),
      new Vector3(points2D[i + 2], points2D[i + 3], 0).multiply(scale),
      new Vector3(points2D[i + 4] ?? lastX, points2D[i + 5] ?? lastY, 0).multiply(scale),
      new Vector3(points2D[i + 6] ?? lastX, points2D[i + 7] ?? lastY, 0).multiply(scale),
    );

    curve.add(curveSection);
  }

  return curve;
};

/**
 * Calculates the normal direction in the vertex connecting two straight lines. The two connected lines
 * are represented by three consecutive input points, the mid point being the one to calculate the normal for.
 * The output vector is always facing to the left side of the connected lines.
 *
 * @param {Vector3} prevPoint First point in the sequence of vertices
 * @param {Vector3} currentPoint  Mid point in the sequence of vertices, normal starts in this point
 * @param {Vector3} nextPoint The last point in the sequence of vertices
 * @param {Vector3} outputVector Vector to write the direction of the normal in
 *
 */
export const getVertexNormal = (
  prevPoint: Vector3 | undefined,
  currentPoint: Vector3,
  nextPoint: Vector3 | undefined,
) => {
  const normal = new Vector3();

  if (prevPoint && nextPoint) {
    const previousDirection = new Vector2(
      currentPoint.x - prevPoint.x,
      currentPoint.y - prevPoint.y,
    ).normalize();
    const nextDirection = new Vector2(
      nextPoint.x - currentPoint.x,
      nextPoint.y - currentPoint.y,
    ).normalize();

    const normalDirection = nextDirection.clone().sub(previousDirection).normalize();

    if (isVectorLeftOf(nextDirection, normalDirection)) {
      normal.set(normalDirection.x, normalDirection.y, 0);
    } else {
      normal.set(-normalDirection.x, -normalDirection.y, 0);
    }
  } else if (!prevPoint && nextPoint) {
    normal
      .copy(nextPoint)
      .sub(currentPoint)
      .applyAxisAngle(Z_VECTOR_3, Math.PI / 2)
      .normalize();
  } else if (!nextPoint && prevPoint) {
    normal
      .copy(currentPoint)
      .sub(prevPoint)
      .applyAxisAngle(Z_VECTOR_3, Math.PI / 2)
      .normalize();
  }

  return normal;
};

/**
 * Implementation of the Ramer-Douglas-Peucker algorithm for simplifying arrays of points defining a curve.
 * See https://en.wikipedia.org/wiki/Ramer%E2%80%93Douglas%E2%80%93Peucker_algorithm
 *
 * @param {Vector3[]} points A sequence of points, representing a curve
 * @param {number} epsilon The threshold for further simplification. Smaller epsilon will return more points.
 *
 * @returns {Vector3[]} Simplified curve points
 */
export const simplifyCurvePoints = (points: Vector3[], epsilon: number) => {
  if (points.length === 0) return [];
  let maxDistance = 0;
  let maxIndex = 0;
  const end = points.length - 1;

  // Find the point with the maximum distance
  for (let i = 1; i < end - 1; i++) {
    const distance = distanceFromLineToPointSquared(points[i], points[1], points[end]);

    if (distance > maxDistance) {
      maxIndex = i;
      maxDistance = distance;
    }
  }

  let simplifiedPoints: Vector3[];

  // If max distance is greater than epsilon, recursively simplify
  if (maxDistance > epsilon) {
    const firstHalfResults = simplifyCurvePoints(points.slice(0, maxIndex + 1), epsilon);
    const secondHalfResults = simplifyCurvePoints(points.slice(maxIndex, end + 1), epsilon);

    simplifiedPoints = firstHalfResults
      .slice(0, firstHalfResults.length - 1)
      .concat(secondHalfResults);
  } else {
    simplifiedPoints = [points[0], points[end]];
  }

  return simplifiedPoints;
};

/**
 * Calculates the squared perpendicular distance between a point and a line represented by start and end points.
 * Only applicable in cases with relative comparison of distances - calculating squared distance is
 * faster than caculating real distance.
 *
 * @param {Vector3} point Point from which the distance is measured
 * @param {Vector3} lineStart Line start point
 * @param {Vector3} lineEnd Line end point
 *
 * @returns {number} Squared perpendicular distance from point to line
 *
 */
export const distanceFromLineToPointSquared = (
  point: Vector3,
  lineStart: Vector3,
  lineEnd: Vector3,
) => {
  const D = lineEnd.clone().sub(lineStart).normalize();
  const d = point.clone().sub(lineStart).dot(D);
  const X = lineStart.clone().add(D.clone().multiplyScalar(d));
  X.sub(point);

  return X.x ** 2 + X.y ** 2;
};
